/* eslint-disable */
import React, { FC, useContext, useEffect } from 'react';
import { UserManager, UserManagerSettings, WebStorageStateStore, User } from 'oidc-client';
import * as AuthActions from '../../../store/actions/auth';
import { useStoreContext } from '../../App/StoreProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { Routes } from '../../../utils';
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import { AuthState } from '../../../store/types';

const config: UserManagerSettings = {
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    authority: import.meta.env.VITE_AUTHORITY_URL,
    client_id: import.meta.env.VITE_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_REDIRECT_URL,
    automaticSilentRenew: true,
    monitorSession: true,
    revokeAccessTokenOnSignout: true,
    silent_redirect_uri: import.meta.env.VITE_SILENT_REDIRECT_URL,
    post_logout_redirect_uri: import.meta.env.VITE_POST_LOGOUT_URL,
    response_type: 'id_token token',
    scope: 'openid profile SandBox.Job SandBox.Load SandBox.AssetLoad SandBox.User SandBox.Message SandBox.Driver',
};

const _userManager = new UserManager(config);


const AuthContext = React.createContext({
    userManager: _userManager,
    userHasPermission: (permissionType: PermissionEnum): boolean => false,
});

const AuthConsumer = AuthContext.Consumer;

const useAuthContext = (): {
    userManager: UserManager;
    userHasPermission: (permissionType: PermissionEnum) => boolean;
} => useContext(AuthContext);

interface AuthProviderProps {
    children: React.ReactElement;
}

const AuthProvider: FC<AuthProviderProps> = (props: AuthProviderProps): React.ReactElement => {
    const { children } = props;
    const userManager = _userManager;
    const [{ auth }, dispatch] = useStoreContext();
  
    
    const userHasPermission = (permissionType: PermissionEnum): boolean => {
        let hasPermission;

        try {
            if (auth.user) {
                var perms = JSON.parse(auth!.user!.profile!.permissions);
                hasPermission = perms.includes(permissionType.toString());
            }
        } catch (error) {
            
             console.log(error);
        }

        return hasPermission;
    };

    const loadUser = () => {
        userManager.getUser().then(user => {
           
            if (user) {
                let payload: AuthState = {
                    user: user,
                    isUserLoading: false,
                    signalrConnection: auth.signalrConnection
                }
                dispatch(AuthActions.userLoaded(payload));
                if (window.location.origin + '/' === window.location.href) {
                    if (window.screen.width > 1199) {
                        window.location.href = `${window.location.origin}${Routes.JOB_MANAGEMENT.route}`;
                    } else {
                        window.location.href = `${window.location.origin}${Routes.JOB_MANAGEMENT_FOR_TAB.route}`;
                    }
                }
            } else {
                
                dispatch(AuthActions.userLoading(false));
            }
        });
    };

    const onUserLoaded = (user: User): void => {
        localStorage.setItem('accessToken', user.access_token);
        
       // // debugger;
        const newConnection = new HubConnectionBuilder()
            .withUrl(import.meta.env.VITE_NOTIFICATION_HUB_URL + 'hub', {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .build();

        let payload: AuthState = {
            user: user,
            isUserLoading: false,
            signalrConnection: newConnection,
        }

        dispatch(AuthActions.userLoaded(payload));
    }

    const onSilentRenewError = (e: any): void => {
        dispatch(AuthActions.silentRenewError(e));
    };

    const onAccessTokenExpired = (): void => {
        dispatch(AuthActions.accessTokenExpired());
        userManager.removeUser();
    };

    const onUserUnloaded = (): void => {
        dispatch(AuthActions.userUnloaded());
        localStorage.removeItem('accessToken');
        localStorage.removeItem('url');
        window.location.href = window.location.origin;
    };

    const onUserSignedOut = (): void => {
        dispatch(AuthActions.userSignedOut());
        userManager.removeUser();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('url');
        window.location.href = window.location.origin;
    };

    useEffect(() => {
        loadUser();
        userManager.events.addUserLoaded(onUserLoaded);
        userManager.events.addSilentRenewError(onSilentRenewError);
        userManager.events.addAccessTokenExpired(onAccessTokenExpired);
        userManager.events.addUserUnloaded(onUserUnloaded);
        userManager.events.addUserSignedOut(onUserSignedOut);

        return () => {
            userManager.events.removeUserLoaded(onUserLoaded);
            userManager.events.removeSilentRenewError(onSilentRenewError);
            userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
            userManager.events.removeUserUnloaded(onUserUnloaded);
            userManager.events.removeUserSignedOut(onUserSignedOut);
        };
    }, []);

    return <AuthContext.Provider value={{ userManager, userHasPermission }}>{children}</AuthContext.Provider>;
};

export { AuthConsumer, useAuthContext, AuthProvider };
